import React, { useState } from 'react';
import styled from 'styled-components';
import { signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../services/firebaseConfig';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: var(--color-white);
  border: 2px solid var(--color-light-gray);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--color-black);
  text-align: center;
  margin-bottom: 10px;
  letter-spacing: 1px;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--color-gray);
  text-align: center;
  margin-bottom: 30px;
`;

const InfoContainer = styled.div`
  margin-bottom: 24px;
`;

const InfoText = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 8px;
  padding-left: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
`;

const LoadingText = styled.p`
  font-size: 1rem;
  color: #666;
  margin-top: 12px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GoogleButton = styled.button`
  background-color: var(--color-black);
  color: var(--color-white);
  border: none;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  
  &:hover {
    background-color: var(--color-blue);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.3);
  }
`;

const GoogleText = styled.span`
  font-weight: 700;
`;

const CancelButton = styled.button`
  background-color: transparent;
  color: var(--color-blue);
  border: 2px solid var(--color-blue);
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  
  &:hover {
    background-color: var(--color-blue);
    color: var(--color-white);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.3);
  }
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4285F4;
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 0.9rem;
  text-align: center;
`;

interface GoogleLoginModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: (userData: any) => void;
}

const GoogleLoginModal: React.FC<GoogleLoginModalProps> = ({ visible, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('Starting Google login...');
      console.log('Firebase Auth status:', auth);
      console.log('Google Provider config:', googleProvider);
      
      // 使用 Firebase Google 登录
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      console.log('Google login successful:', user);
      console.log('Google user details:', {
        uid: user.uid,
        displayName: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified
      });
      
      // 🔥 关键部分：检查并创建 Firestore 用户文档
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      let userData;
      
      if (!userDoc.exists()) {
        // New user: create user document in Firestore
        console.log('New user, creating Firestore document:', user.uid);
        
        const newUserData = {
          id: user.uid,
          username: user.displayName || 'Google User',
          email: user.email || user.providerData?.[0]?.email || '',
          avatar: user.photoURL || '/uploads/demo-avatar.png',
          provider: 'google.com',
          emailVerified: user.emailVerified,
          createdAt: new Date(),
          updatedAt: new Date(),
          lastLoginAt: new Date(),
          credits: 5
        };
        
        // Save to Firestore
        await setDoc(userDocRef, newUserData);
        
        // Build return data
        userData = {
          ...newUserData,
          createdAt: newUserData.createdAt.toISOString(),
          updatedAt: newUserData.updatedAt.toISOString(),
          lastLoginAt: newUserData.lastLoginAt.toISOString(),
          firebaseUser: user
        };
        
        console.log('✅ New user Firestore document created');
      } else {
        // Existing user: get existing data from Firestore
        console.log('Existing user login:', user.uid);
        
        const existingData = userDoc.data();
        userData = {
          id: existingData.id,
          username: existingData.username,
          email: existingData.email,
          avatar: existingData.avatar,
          provider: existingData.provider,
          emailVerified: existingData.emailVerified,
          createdAt: existingData.createdAt.toDate().toISOString(),
          updatedAt: existingData.updatedAt.toDate().toISOString(),
          lastLoginAt: existingData.lastLoginAt.toDate().toISOString(),
          credits: existingData.credits,
          firebaseUser: user
        };
        
        // Update last login time
        await setDoc(userDocRef, {
          lastLoginAt: new Date(),
          updatedAt: new Date()
        }, { merge: true });
        
        console.log('✅ Existing user data retrieved from Firestore');
      }
      
      console.log('Final user data:', userData);
      
      setLoading(false);
      onSuccess(userData);
      onClose();
    } catch (error: any) {
      console.error('Google login failed:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Google login failed, please try again';
      
      if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Google login is not enabled, please contact the administrator';
      } else if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Login was cancelled';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup blocked, please allow popups and try again';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error, please check your connection';
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!visible) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Title>ACCORD</Title>
        <Subtitle>
          Sign in with Google Account
        </Subtitle>
      
        {error && <ErrorMessage>{error}</ErrorMessage>}

        {loading ? (
          <LoadingContainer>
            <Spinner />
            <LoadingText>Connecting to Google...</LoadingText>
          </LoadingContainer>
        ) : (
          <ButtonContainer>
            <GoogleButton onClick={handleGoogleLogin}>
              Login with <GoogleText>
                <span style={{ color: '#4285F4' }}>G</span>
                <span style={{ color: '#EA4335' }}>O</span>
                <span style={{ color: '#FBBC05' }}>O</span>
                <span style={{ color: '#4285F4' }}>G</span>
                <span style={{ color: '#34A853' }}>L</span>
                <span style={{ color: '#EA4335' }}>E</span>
              </GoogleText>
            </GoogleButton>
            
            <CancelButton onClick={handleCancel}>
              Cancel
            </CancelButton>
          </ButtonContainer>
        )}
      </Modal>
    </Overlay>
  );
};

export default GoogleLoginModal;
